import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { EditableSelect, EditableTextarea } from "@/blocks/Editable";

import {
  CustomSummaryResultFragment,
  SummaryTemplateResult,
  SummaryTemplateType,
} from "@/graphql/generated/graphql";

type SummaryTemplate =
  CustomSummaryResultFragment["customSummary"]["templates"][number]["template"];

type CustomSummaryResultsFormFieldSelectProps = {
  content: string;
  template?: SummaryTemplate;
  onUpdate: (newValue: string) => void;
};

function CustomSummaryResultsFormFieldSelect({
  content,
  template,
  onUpdate,
}: CustomSummaryResultsFormFieldSelectProps) {
  const { t } = useTranslation();

  const options = useMemo(() => {
    if (!template?.response) return [];

    if (template.response.__typename === "OptionsResponse") {
      return template.response.options.map((option) => ({
        label: option.value,
        value: option.value,
      }));
    }

    if (template.response.__typename === "ValidationResponse") {
      return template.response.validationOptions.map((option) => ({
        label: t(`custom_summary.validation_options.${option.value}`),
        value: option.value,
      }));
    }

    return [];
  }, [template, t]);

  return (
    <EditableSelect<string>
      defaultValue={content ?? undefined}
      placeholder={t("call_context.summary_empty_message")}
      onChange={onUpdate}
      options={options}
    />
  );
}

type EditableFieldProps = {
  type: SummaryTemplateResult["type"];
  content: string;
  template?: SummaryTemplate;
  onUpdate: (newValue: string) => void;
};

function EditableField({
  type,
  content,
  template,
  onUpdate,
}: EditableFieldProps) {
  const { t } = useTranslation();

  switch (type) {
    case SummaryTemplateType.DISCOVERY:
      return (
        <EditableTextarea
          placeholder={t("PlaybookSummary.Empty")}
          defaultValue={content ?? undefined}
          onSubmit={(newValue) => onUpdate(newValue)}
          className="text-sm font-normal"
          maxLength={1024}
        />
      );
    case SummaryTemplateType.OPTIONS:
    case SummaryTemplateType.VALIDATION:
      return (
        <CustomSummaryResultsFormFieldSelect
          content={content}
          template={template}
          onUpdate={onUpdate}
        />
      );
    default: {
      // Exhaustiveness guard: a new SummaryTemplateType fails the typecheck here.
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
}

type CustomSummaryResultsFormFieldProps = {
  templateResult: SummaryTemplateResult;
  template?: SummaryTemplate;
  onUpdate: (newValue: string) => void;
  readOnly?: boolean;
};

export function CustomSummaryResultsFormField({
  template,
  templateResult,
  onUpdate,
  readOnly = false,
}: CustomSummaryResultsFormFieldProps) {
  const { summaryTemplateName, content, type } = templateResult;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-accent-foreground text-sm font-semibold">
        {summaryTemplateName}
      </span>

      {readOnly ? (
        <span className="whitespace-pre-line text-foreground text-sm font-normal">
          {content}
        </span>
      ) : (
        <EditableField
          type={type}
          content={content}
          template={template}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
}
